import React, { useState, useEffect } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  Animated,
  TouchableWithoutFeedback,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { BlurView } from 'expo-blur';
import useTheme from '../../hooks/useTheme';

interface PromptButton {
  text: string;
  onPress?: (inputText: string) => void;
  style?: 'default' | 'cancel' | 'destructive';
}

interface PromptData {
  title: string;
  message?: string;
  inputType?: 'plain-text' | 'secure-text';
  placeholder?: string;
  buttons: PromptButton[];
  dismissable?: boolean;
}

class PromptManager {
  private static instance: PromptManager;
  private showPrompt?: (data: PromptData) => void;

  static getInstance(): PromptManager {
    if (!PromptManager.instance) {
      PromptManager.instance = new PromptManager();
    }
    return PromptManager.instance;
  }

  setShowPrompt(showPrompt: (data: PromptData) => void) {
    this.showPrompt = showPrompt;
  }

  prompt(
    title: string,
    message?: string,
    buttons: PromptButton[] = [],
    inputType: 'plain-text' | 'secure-text' = 'plain-text',
    placeholder?: string
  ) {
    if (this.showPrompt) {
      this.showPrompt({ title, message, inputType, placeholder, buttons });
    }
  }
}

export const CustomPrompt = {
  prompt: (
    title: string,
    message?: string,
    buttons: PromptButton[] = [],
    inputType: 'plain-text' | 'secure-text' = 'plain-text',
    placeholder?: string
  ) => {
    PromptManager.getInstance().prompt(title, message, buttons, inputType, placeholder);
  },
};

export const CustomPromptProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { colors, isDark } = useTheme();
  const [promptData, setPromptData] = useState<PromptData | null>(null);
  const [visible, setVisible] = useState(false);
  const [inputText, setInputText] = useState('');
  const fadeAnim = useState(new Animated.Value(0))[0];
  const scaleAnim = useState(new Animated.Value(0.8))[0];

  useEffect(() => {
    PromptManager.getInstance().setShowPrompt((data: PromptData) => {
      setPromptData(data);
      setVisible(true);
      setInputText('');
    });
  }, []);

  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.spring(scaleAnim, {
          toValue: 1,
          friction: 8,
          tension: 40,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 150,
          useNativeDriver: true,
        }),
        Animated.timing(scaleAnim, {
          toValue: 0.8,
          duration: 150,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [visible]);

  const handleClose = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 150,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 0.8,
        duration: 150,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setVisible(false);
      setPromptData(null);
      setInputText('');
    });
  };

  const handleButtonPress = async (button: PromptButton) => {
    if (button.onPress) {
      await button.onPress(inputText);
    }
    handleClose();
  };

  const getButtonStyle = (style?: string) => {
    switch (style) {
      case 'cancel':
        return {
          backgroundColor: colors.surface,
          borderColor: colors.border,
          borderWidth: 1,
        };
      case 'destructive':
        return {
          backgroundColor: '#EF4444',
        };
      default:
        return {
          backgroundColor: colors.primary,
        };
    }
  };

  const getButtonTextStyle = (style?: string) => {
    switch (style) {
      case 'cancel':
        return { color: colors.text };
      default:
        return { color: 'white' };
    }
  };

  if (!promptData || !visible) {
    return <>{children}</>;
  }

  const { dismissable = false } = promptData;

  return (
    <>
      {children}
      <Modal
        transparent
        visible={visible}
        animationType="none"
        onRequestClose={dismissable ? handleClose : undefined}
        statusBarTranslucent
      >
        <TouchableWithoutFeedback onPress={dismissable ? handleClose : undefined}>
          <View style={styles.overlay}>
            {Platform.OS === 'ios' && (
              <BlurView intensity={20} tint={isDark ? 'dark' : 'light'} style={StyleSheet.absoluteFillObject} />
            )}
            {Platform.OS !== 'ios' && (
              <Animated.View
                style={[
                  StyleSheet.absoluteFillObject,
                  {
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    opacity: fadeAnim,
                  },
                ]}
              />
            )}
            <KeyboardAvoidingView
              behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
              style={styles.keyboardAvoidingView}
            >
              <TouchableWithoutFeedback>
                <Animated.View
                  style={[
                    styles.promptContainer,
                    {
                      backgroundColor: colors.surface,
                      opacity: fadeAnim,
                      transform: [{ scale: scaleAnim }],
                      shadowColor: colors.text,
                    },
                  ]}
                >
                  {/* Icon */}
                  <View style={[styles.iconContainer, { backgroundColor: colors.background }]}>
                    <Ionicons name="create-outline" size={48} color={colors.primary} />
                  </View>

                  {/* Title */}
                  <Text style={[styles.title, { color: colors.text }]}>{promptData.title}</Text>

                  {/* Message */}
                  {promptData.message && (
                    <Text style={[styles.message, { color: colors.textSecondary }]}>{promptData.message}</Text>
                  )}

                  {/* Input */}
                  <TextInput
                    style={[
                      styles.input,
                      {
                        backgroundColor: colors.background,
                        color: colors.text,
                        borderColor: colors.border,
                      },
                    ]}
                    placeholder={promptData.placeholder}
                    placeholderTextColor={colors.textSecondary}
                    value={inputText}
                    onChangeText={setInputText}
                    secureTextEntry={promptData.inputType === 'secure-text'}
                    autoFocus
                  />

                  {/* Buttons */}
                  <View style={styles.buttonContainer}>
                    {promptData.buttons.map((button, index) => (
                      <TouchableOpacity
                        key={index}
                        style={[
                          styles.button,
                          getButtonStyle(button.style),
                          promptData.buttons.length > 1 && index === 0 && styles.firstButton,
                        ]}
                        onPress={() => handleButtonPress(button)}
                        activeOpacity={0.8}
                      >
                        <Text style={[styles.buttonText, getButtonTextStyle(button.style)]}>
                          {button.text}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </Animated.View>
              </TouchableWithoutFeedback>
            </KeyboardAvoidingView>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  keyboardAvoidingView: {
    width: '100%',
    maxWidth: 340,
  },
  promptContainer: {
    width: '100%',
    borderRadius: 20,
    padding: 24,
    alignItems: 'center',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 8,
  },
  message: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 22,
  },
  input: {
    width: '100%',
    borderWidth: 1,
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
    fontSize: 16,
    textAlign: 'center',
  },
  buttonContainer: {
    width: '100%',
    gap: 12,
  },
  button: {
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  firstButton: {
    marginBottom: 0,
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default CustomPromptProvider;